/**
 * Sprint v317 diagnostic event channel.
 *
 * Entries are intentionally limited to UI interaction state: semantic target,
 * touch/pointer phase, UI rectangle, scroll owner, and scroll delta. They do
 * not include customer data, text content, geographic coordinates, or input
 * values, and they are never persisted or sent to an API.
 */
export const MOBILE_SCROLL_TRACE_EVENT = "crs:mobile-scroll-trace";

export type MobileScrollTraceEntry = {
  phase: string;
  detail: string;
};

export type DashboardTouchZone = "fab-center" | "fab-left" | "fab-top" | "fab-right" | "content-right";

type UiRect = Pick<DOMRect, "left" | "top" | "width" | "height">;

/**
 * Classifies only a semantic Dashboard gesture zone. Coordinates are used
 * locally for the calculation and are never returned, emitted, persisted, or
 * sent to a server.
 */
export function getDashboardTouchZone(
  point: Pick<Touch, "clientX" | "clientY">,
  fabRect: UiRect | null,
  viewportWidth: number,
): DashboardTouchZone {
  if (!fabRect) return "content-right";

  const right = fabRect.left + fabRect.width;
  const bottom = fabRect.top + fabRect.height;
  const inFab = point.clientX >= fabRect.left
    && point.clientX <= right
    && point.clientY >= fabRect.top
    && point.clientY <= bottom;
  if (inFab) return "fab-center";

  const withinFabVerticalBand = point.clientY >= fabRect.top - 20 && point.clientY <= bottom + 20;
  if (withinFabVerticalBand && point.clientX < fabRect.left && point.clientX >= fabRect.left - 72) return "fab-left";
  if (withinFabVerticalBand && point.clientX > right && point.clientX <= right + 72) return "fab-right";

  const withinFabHorizontalBand = point.clientX >= fabRect.left - 20 && point.clientX <= right + 20;
  if (withinFabHorizontalBand && point.clientY < fabRect.top && point.clientY >= fabRect.top - 72) return "fab-top";

  return point.clientX >= viewportWidth * 0.65 ? "content-right" : "content-right";
}

export function emitMobileScrollTrace(entry: MobileScrollTraceEntry): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<MobileScrollTraceEntry>(MOBILE_SCROLL_TRACE_EVENT, { detail: entry }));
}

export function formatMobileUiRect(rect: Pick<DOMRect, "left" | "top" | "width" | "height">): string {
  const px = (value: number) => Math.round(value);
  return `uiRect=${px(rect.left)},${px(rect.top)},${px(rect.width)}×${px(rect.height)}`;
}
